import { useState } from 'react';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import type { PublicacaoResponse, SituacaoPublicacao } from '@triade/contracts';
import { ROTULO_SITUACAO_PUBLICACAO } from '@triade/contracts';
import { useListaPublicacoes } from '@/api/publicacoes';
import { Button } from '@/components/ui/button';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { formatarContagem, formatarData, formatarDataHora } from '@/lib/format';
import { PublicacoesCompararDialog } from './publicacoes-comparar-dialog';
import { MensagemPublicacoes, SeloSituacao, vigenciaTexto } from './publicacoes-util';

/** Maximo de publicacoes marcadas para comparar. */
export const LIMITE_COMPARAR = 2;

type FiltroSituacao = 'TODAS' | SituacaoPublicacao;
const SITUACOES: SituacaoPublicacao[] = ['PROGRAMADA', 'VIGENTE', 'ENCERRADA'];

export interface PublicacoesListaProps {
  onAbrir: (id: string) => void;
}

/** Lista de publicacoes (mais recentes primeiro), filtro por situacao, marcar ate 2 para comparar. */
export function PublicacoesLista({ onAbrir }: PublicacoesListaProps) {
  const lista = useListaPublicacoes({});
  const [filtro, setFiltro] = useState<FiltroSituacao>('TODAS');
  const [marcadas, setMarcadas] = useState<string[]>([]);
  const [comparar, setComparar] = useState<{ a: string; b: string } | null>(null);

  const todas = lista.data ?? [];
  const visiveis = filtro === 'TODAS' ? todas : todas.filter((p) => p.situacao === filtro);
  const pagPub = usePaginarLista(visiveis);

  function alternar(id: string) {
    setMarcadas((atual) => {
      if (atual.includes(id)) return atual.filter((x) => x !== id);
      return atual.length < LIMITE_COMPARAR ? [...atual, id] : atual;
    });
  }

  function abrirComparacao() {
    const [a, b] = marcadas;
    if (a && b) setComparar({ a, b });
  }

  const podeComparar = marcadas.length === LIMITE_COMPARAR;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3 px-5 pt-4">
        <label className="flex flex-col gap-1 text-caption text-ink-muted">
          Situação
          <select
            aria-label="Filtrar por situação"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value as FiltroSituacao)}
            className="h-9 rounded-lg border border-input bg-background px-3 text-small text-ink"
          >
            <option value="TODAS">Todas</option>
            {SITUACOES.map((s) => (
              <option key={s} value={s}>
                {ROTULO_SITUACAO_PUBLICACAO[s]}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-3">
          <span className="text-caption text-ink-muted">
            {podeComparar ? `${LIMITE_COMPARAR} marcadas` : `Marque até ${LIMITE_COMPARAR} para comparar`}
          </span>
          <Button type="button" variant="outline" disabled={!podeComparar} onClick={abrirComparacao}>
            Comparar
          </Button>
        </div>
      </div>

      {lista.isPending && <MensagemPublicacoes papel="status">Carregando publicações…</MensagemPublicacoes>}
      {!lista.isPending && lista.isError && (
        <div className="px-5 pb-4">
          <ErroFormacao erro={lista.error} titulo="Não foi possível carregar as publicações" onTentar={() => void lista.refetch()} />
        </div>
      )}
      {!lista.isPending && !lista.isError && todas.length === 0 && (
        <MensagemPublicacoes>Nenhuma publicação gravada ainda. Ao publicar a lista de preços, ela aparece aqui.</MensagemPublicacoes>
      )}
      {!lista.isPending && !lista.isError && todas.length > 0 && visiveis.length === 0 && (
        <MensagemPublicacoes>Nenhuma publicação com essa situação.</MensagemPublicacoes>
      )}
      {!lista.isPending && !lista.isError && visiveis.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse text-small">
            <thead>
              <tr className="text-left text-caption text-ink-muted">
                <th className="w-10 px-5 py-2.5 font-medium">
                  <span className="sr-only">Comparar</span>
                </th>
                <th className="px-3 py-2.5 font-medium">Vigência</th>
                <th className="px-3 py-2.5 font-medium">Situação</th>
                <th className="px-3 py-2.5 font-medium">Quem publicou</th>
                <th className="px-3 py-2.5 text-right font-medium">Itens</th>
                <th className="px-3 py-2.5 text-right font-medium">Preços</th>
                <th className="px-3 py-2.5 font-medium">Observação</th>
                <th className="px-5 py-2.5 text-right font-medium">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pagPub.visiveis.map((p) => (
                <LinhaPublicacao
                  key={p.id}
                  publicacao={p}
                  marcada={marcadas.includes(p.id)}
                  desabilitada={!marcadas.includes(p.id) && marcadas.length >= LIMITE_COMPARAR}
                  onAlternar={() => alternar(p.id)}
                  onAbrir={() => onAbrir(p.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {visiveis.length > 0 && <BarraDeAcoesLista paginacao={pagPub.paginacao} />}

      <PublicacoesCompararDialog
        a={comparar?.a ?? null}
        b={comparar?.b ?? null}
        onOpenChange={(aberto) => {
          if (!aberto) setComparar(null);
        }}
      />
    </div>
  );
}

function LinhaPublicacao({
  publicacao: p,
  marcada,
  desabilitada,
  onAlternar,
  onAbrir,
}: {
  publicacao: PublicacaoResponse;
  marcada: boolean;
  desabilitada: boolean;
  onAlternar: () => void;
  onAbrir: () => void;
}) {
  return (
    <tr data-testid="linha-publicacao" className="border-t border-line-soft align-top">
      <td className="px-5 py-2.5">
        <input
          type="checkbox"
          aria-label={`Comparar publicação ${vigenciaTexto(p)}`}
          checked={marcada}
          disabled={desabilitada}
          onChange={onAlternar}
          className="size-4 accent-primary"
        />
      </td>
      <td className="tabular px-3 py-2.5 whitespace-nowrap">{vigenciaTexto(p)}</td>
      <td className="px-3 py-2.5">
        <SeloSituacao situacao={p.situacao} />
      </td>
      <td className="px-3 py-2.5">
        <div>{p.publicadoPorNome ?? '—'}</div>
        <div className="text-caption text-ink-muted">{formatarDataHora(p.publicadaEm)}</div>
      </td>
      <td className="tabular px-3 py-2.5 text-right">{formatarContagem(p.totalItens)}</td>
      <td className="tabular px-3 py-2.5 text-right">{formatarContagem(p.totalPrecos)}</td>
      <td className="max-w-[260px] px-3 py-2.5 text-ink-body">{p.observacao ?? <span className="text-ink-muted">—</span>}</td>
      <td className="px-5 py-2.5 text-right">
        <Button type="button" variant="ghost" size="sm" onClick={onAbrir} aria-label={`Abrir publicação ${formatarData(p.validFrom)}`}>
          Abrir
        </Button>
      </td>
    </tr>
  );
}
